import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import authService from "../services/authService";
import { consumeAuthRedirectNotice } from "../utils/authRedirect";

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const labelClassName =
  "mb-2 flex items-center gap-1 text-sm font-semibold text-slate-200";

const RequiredMark = () => (
  <span className="text-sm font-semibold text-rose-400" aria-hidden="true">
    *
  </span>
);

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [captcha, setCaptcha] = useState(() =>
    Math.floor(1000 + Math.random() * 9000)
  );
  const [captchaInput, setCaptchaInput] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const notice = consumeAuthRedirectNotice("/login");

    if (notice) {
      setModal({
        isOpen: true,
        type: "error",
        message: notice,
      });
    }
  }, []);

  const refreshCaptcha = () => {
    setCaptcha(Math.floor(1000 + Math.random() * 9000));
    setCaptchaInput("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (captchaInput.trim() !== captcha.toString()) {
      setModal({
        isOpen: true,
        type: "error",
        message: "Invalid captcha. Please try again.",
      });
      refreshCaptcha();
      return;
    }

    setLoading(true);

    try {
      const response = await authService.login(
        formData.email,
        formData.password
      );

      if (response.status === "success") {
        navigate("/research", { replace: true });
        return;
      } else {
        setModal({
          isOpen: true,
          type: "error",
          message: response.message || "Login failed. Please try again.",
        });
      }
    } catch (error) {
      setModal({
        isOpen: true,
        type: "error",
        message:
          error.message || "An error occurred during login. Please try again.",
      });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const modalClassName =
    modal.type === "error"
      ? "border-rose-400/35 bg-rose-500/10 text-rose-100"
      : "border-emerald-400/35 bg-emerald-500/10 text-emerald-100";

  return (
    <div className="flex min-h-screen flex-col bg-[#111318] text-slate-200">
      <Header />

      <main className="flex-1">
        <section className="flex justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-[560px] rounded-[24px] border border-slate-700/70 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-5 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8"
          >
            <div className="mb-6 text-center sm:mb-7">
              <h2 className="text-2xl font-bold tracking-[-0.02em] text-slate-50 sm:text-[30px]">
                Researcher Login
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Sign in to continue exploring Limbus Intelligence.
              </p>
            </div>

            {modal.isOpen && (
              <div
                className={`mb-5 rounded-2xl border px-4 py-3 text-sm leading-6 whitespace-pre-line ${modalClassName}`}
                role="alert"
              >
                {modal.message}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="email" className={labelClassName}>
                <span>Email</span>
                <RequiredMark />
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClassName}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="password" className={labelClassName}>
                Password
                <RequiredMark />
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`${inputClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-[13px] font-medium text-blue-400 transition hover:text-blue-300"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="captcha" className={labelClassName}>
                Captcha
                <RequiredMark />
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="captcha"
                  type="text"
                  placeholder="Enter captcha"
                  value={captchaInput}
                  onChange={(event) => setCaptchaInput(event.target.value)}
                  required
                  className={inputClassName}
                />

                <div className="flex min-w-[148px] items-center justify-between rounded-xl bg-blue-600 px-4 py-3 font-semibold tracking-[0.22em] text-white sm:justify-center sm:gap-3">
                  <span className="text-sm">{captcha}</span>
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    title="Refresh Captcha"
                    className="rounded-full bg-white/10 px-2 py-1 text-sm tracking-normal transition hover:bg-white/20"
                  >
                    ↻
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-5">
              <label className="flex items-start gap-3 text-sm leading-6 text-slate-300">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(event) => setAcceptTerms(event.target.checked)}
                  className="mt-1 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500"
                />
                <span>
                  I agree to the{" "}
                  <Link
                    to="/legal-docs"
                    className="font-medium text-blue-400 underline-offset-2 hover:underline"
                  >
                    Terms & Conditions
                  </Link>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={!acceptTerms || loading}
              className="w-full rounded-xl bg-blue-600 px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600"
            >
              {loading ? "Logging in..." : "Login"}
            </button>

            <div className="mt-4 text-center">
              <Link
                to="/admin/login"
                replace={true}
                className="inline-flex rounded-xl border border-blue-500/30 bg-blue-500/10 px-6 py-3 text-[15px] font-semibold text-blue-400 transition hover:border-blue-400/50 hover:bg-blue-500/15"
              >
                Admin Login
              </Link>
            </div>

            <div className="mt-5 text-center text-sm text-slate-400">
              Don&apos;t have an account?{" "}
              <Link
                to="/signup"
                className="font-semibold text-blue-400 transition hover:text-blue-300"
              >
                Sign Up
              </Link>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
